import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { USER_SAVES_QUERY, USER_MOCK_SAVES_QUERY } from '../graphql/queries';
import { CREATE_MOCK_SAVE, DELETE_MOCK_SAVE } from '../graphql/mutations';
import { useAuth } from './auth';

interface SavedContentContextType {
  saves: any[];
  mockSaves: any[];
  isLoading: boolean;
  error: any;
  refetch: () => Promise<void>;
  handleMockSave: (imageId: string, mockId: string, isSaved: boolean) => Promise<void>;
}

const SavedContentContext = createContext<SavedContentContextType | undefined>(undefined);

export const SavedContentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [saves, setSaves] = useState<any[]>([]);
  const [mockSaves, setMockSaves] = useState<any[]>([]);
  const [pendingSaves, setPendingSaves] = useState<Set<string>>(new Set());
  const { user } = useAuth();

  const {
    data: savesData,
    loading: savesLoading,
    error: savesError,
    refetch: refetchSaves,
  } = useQuery(USER_SAVES_QUERY, {
    skip: !user,
  });

  const {
    data: mockSavesData,
    loading: mockSavesLoading,
    error: mockSavesError,
    refetch: refetchMockSaves,
  } = useQuery(USER_MOCK_SAVES_QUERY, {
    skip: !user,
  });

  const [createMockSave] = useMutation(CREATE_MOCK_SAVE);
  const [deleteMockSave] = useMutation(DELETE_MOCK_SAVE);

  useEffect(() => {
    if (savesData?.userSaves) {
      setSaves(savesData.userSaves);
    }
  }, [savesData]);

  useEffect(() => {
    if (mockSavesData?.userMockSaves) {
      setMockSaves(mockSavesData.userMockSaves);
    }
  }, [mockSavesData]);

  const handleMockSave = async (imageId: string, mockId: string, isSaved: boolean) => {
    const saveKey = `${imageId}-${mockId}`;

    try {
      // If there's already a pending operation for this save, skip
      if (pendingSaves.has(saveKey)) {
        return;
      }

      // Add to pending saves
      setPendingSaves((prev) => new Set(prev).add(saveKey));

      if (isSaved) {
        // Check if save already exists
        const existingSave = mockSaves.find((save) => save.imageId === imageId && save.mockId === mockId);

        if (!existingSave) {
          await createMockSave({
            variables: {
              input: {
                imageId,
                mockId,
              },
            },
          });
        }
      } else {
        await deleteMockSave({
          variables: {
            input: {
              imageId,
              mockId,
            },
          },
        });
      }

      // Refetch after mutation
      await refetchMockSaves();
    } catch (error) {
      console.error('Error handling mock save:', error);
      throw error;
    } finally {
      // Remove from pending saves
      setPendingSaves((prev) => {
        const next = new Set(prev);
        next.delete(saveKey);
        return next;
      });
    }
  };

  const refetch = async () => {
    await Promise.all([refetchSaves(), refetchMockSaves()]);
  };

  const value = {
    saves,
    mockSaves,
    isLoading: savesLoading || mockSavesLoading,
    error: savesError || mockSavesError,
    refetch,
    handleMockSave,
  };

  return <SavedContentContext.Provider value={value}>{children}</SavedContentContext.Provider>;
};

export const useSavedContent = () => {
  const context = useContext(SavedContentContext);
  if (context === undefined) {
    throw new Error('useSavedContent must be used within a SavedContentProvider');
  }
  return context;
};
